import { i18n } from "@/i18n";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import CommitmentsTabType from "@/constants/CommitmentsTabType";
import OutcomeStatusV2 from "@/constants/OutcomeStatusV2";
import OutcomeType from "@/constants/OutcomeType";
import OutcomeSortType from "@/constants/OutcomeSortType";
import { meetingAPI } from "@/api";
import OutcomePermissions from "@/permissions/OutcomePermissions";
import { loadAllOutstandingOutcomes, initWebSocketListeners } from "./outcomeApiOrchestrator";

const DEFAULT_PAGE_SIZE = 25;

initWebSocketListeners();

const _state = {
	commitmentsMap: {
		/* Example key/value in the map. The key could be the teamId, meetingId or meetingSeriesId
		"12345678" : {
			id,
			meetingPermissions,
			commitmentsTabType: "TEAM",
			hasInitiallyLoadedOutstanding: false,
			hasInitiallyLoadedCompleted: false,
			hasLoadedOutstanding: false,
			hasLoadedCompleted: false,
			isCancelingLoad: false,
			isChangingCompletionStatus: false,
			isLoadingOutstanding: false,
			isLoadingCompleted: false,
			currentOffsetForCompleted: 0,
			commitments: {},
		}
		*/
	},
};

function getTargetKey( commitmentsTabType ) {
	let key = "meetingId";
	if ( commitmentsTabType === CommitmentsTabType.MEETING_SERIES ) {
		key = "meetingSeriesId";
	}
	if ( [ CommitmentsTabType.TEAM, CommitmentsTabType.ONE_ON_ONE, ].includes( commitmentsTabType ) ) {
		key = "teamId";
	}
	return key;
}

function calculateNewCommitmentsState( { meta, commitments, rootGetters, } ) {
	const timezone = rootGetters[ "auth/timezone" ];
	const userProfile = rootGetters[ "userProfile/userProfile" ];
	return commitments.reduce( ( accum, c ) => {
		const perms = new OutcomePermissions( {
			outcome: c,
			userProfile,
			timezone,
			meetingPermissions: meta.meetingPermissions,
		} );
		c.permissions = perms.resolve();
		accum[ c.id ] = c;
		return accum;
	}, {} );
}

const _getters = {
	commitmentMeta: state => id => {
		return state.commitmentsMap[ id ];
	},
	commitmentMetaContainingOutcomeId: state => outcomeId => {
		return Object.values( state.commitmentsMap )
			.filter( x => {
				return !!x.commitments[ outcomeId ];
			} );
	},
	completedCommitments: state => id => {
		const cState = state.commitmentsMap[ id ];
		return orderBy(
			Object.values( cState.commitments ).filter( x => !!x.completed ),
			[ "completed", "content", ],
			[ "desc", "asc", ]
		);
	},
	hasCommitments: state => id => {
		return !isEmpty( state.commitmentsMap[ id ].commitments );
	},
	hasInitiallyLoadedCompleted: state => id => {
		return !!state.commitmentsMap[ id ].hasInitiallyLoadedCompleted;
	},
	hasInitiallyLoadedOutstanding: state => id => {
		return !!state.commitmentsMap[ id ].hasInitiallyLoadedOutstanding;
	},
	hasLoadedCompleted: state => id => {
		return !!state.commitmentsMap[ id ].hasLoadedCompleted;
	},
	isChangingCompletionStatus: state => id => {
		return !!state.commitmentsMap[ id ].isChangingCompletionStatus;
	},
	isLoadingCompleted: state => id => {
		return !!state.commitmentsMap[ id ].isLoadingCompleted;
	},
	isLoadingOutstanding: state => id => {
		return !!state.commitmentsMap[ id ].isLoadingOutstanding;
	},
	outstandingCommitments: state => id => {
		const cState = state.commitmentsMap[ id ];
		return orderBy(
			Object.values( cState.commitments ).filter( x => !x.completed ),
			[ "dueDate", "created", "content", ]
		);
	},
};

const actions = {
	cancelCommitmentLoading: ( { getters, }, { id, } ) => {
		const meta = getters.commitmentMeta( id );
		meta.outstandingRequest.cancel();
	},
	changeCommitmentCompletion: async ( { commit, getters, rootGetters, }, { id, commitmentId, isCompleted, } ) => {
		let meta = cloneDeep( getters.commitmentMeta( id ) );
		meta.isChangingCompletionStatus = true;
		commit( "commitmentsMetaUpdated", { id, meta, } );
		Nprogress.start();
		try {
			const { meetingId, topicId, } = meta.commitments[ commitmentId ];
			const updatedCommitment = await meetingAPI.saveOutcomeCompletion( {
				meetingId,
				topicId,
				outcomeId: commitmentId,
				isCompleted,
			}, { version: "2", } );
			// we just performed an async operation and underlying state could
			// have changed so we grab our state again to ensure we're current
			meta = cloneDeep( getters.commitmentMeta( id ) );
			meta.commitments = {
				...meta.commitments,
				...calculateNewCommitmentsState( { meta, commitments: [ updatedCommitment, ], rootGetters, } ),
			};
			meta.isChangingCompletionStatus = false;
			commit( "commitmentsMetaUpdated", { id, meta, } );
		} catch ( e ) {
			const message = i18n.t( "commitmentsStore.store_updateCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
		Nprogress.done();
	},
	initializeCommitments: async ( { commit, getters, }, { id, commitmentsTabType, meetingPermissions, } ) => {
		let meta = cloneDeep( getters.commitmentMeta( id ) );
		const outstandingRequest = loadAllOutstandingOutcomes( { id, commitmentsTabType, outcomeStatus: OutcomeStatusV2.OUTSTANDING, } );
		if ( meta ) {
			meta.outstandingRequest = outstandingRequest;
			meta.currentOffsetForCompleted = 0;
			meta.isChangingCompletionStatus = false;
			meta.isLoadingOutstanding = true;
			meta.isLoadingCompleted = false;
		} else {
			meta = {
				id,
				meetingPermissions,
				commitmentsTabType,
				hasInitiallyLoadedOutstanding: false,
				hasInitiallyLoadedCompleted: false,
				hasLoadedOutstanding: false,
				hasLoadedCompleted: false,
				isChangingCompletionStatus: false,
				isLoadingOutstanding: true,
				isLoadingCompleted: false,
				currentOffsetForCompleted: 0,
				commitments: {},
				outstandingRequest,
			};
		}
		commit( "commitmentsMetaUpdated", { id, meta, } );
		outstandingRequest.start();
	},
	// This action handles already-retrieved commitments responses (which originate in outcomeApiOrchestrator)
	loadOutstandingCommitments: async ( { commit, getters, rootGetters, }, { id, commitments, isDone, } ) => {
		const meta = cloneDeep( getters.commitmentMeta( id ) );
		try {
			meta.commitments = {
				...meta.commitments,
				...calculateNewCommitmentsState( { meta, commitments, rootGetters, } ),
			};
			meta.isLoadingOutstanding = !isDone;
			meta.hasInitiallyLoadedOutstanding = true;
			commit( "commitmentsMetaUpdated", { meta, id, } );
		} catch ( e ) {
			meta.isLoadingOutstanding = false;
			commit( "commitmentsMetaUpdated", { id, meta, } );
			const message = i18n.t( "commitmentsStore.store_loadCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
	},
	loadPageOfCompletedCommitments: async ( { commit, getters, rootGetters, }, { id, } ) => {
		let meta = cloneDeep( getters.commitmentMeta( id ) );
		meta.isLoadingCompleted = true;
		commit( "commitmentsMetaUpdated", { id, meta, } );
		try {
			const commitments = await meetingAPI.fetchOutcomes( {
				[ getTargetKey( meta.commitmentsTabType ) ]: id,
				offset: meta.currentOffsetForCompleted,
				limit: DEFAULT_PAGE_SIZE,
				outcomeStatus: OutcomeStatusV2.COMPLETE,
				outcomeType: OutcomeType.COMMITMENT,
				sortType: OutcomeSortType.COMPLETED_RECENTLY,
			} );
			// the state could have changed due to parallel operations (loading outstanding outcomes)
			// so we grab it again now that our API call has completed
			meta = cloneDeep( getters.commitmentMeta( id ) );
			meta.commitments = {
				...meta.commitments,
				...calculateNewCommitmentsState( { meta, commitments, rootGetters, } ),
			};
			meta.isLoadingCompleted = false;
			meta.hasLoadedCompleted = commitments.length !== DEFAULT_PAGE_SIZE;
			meta.currentOffsetForCompleted += DEFAULT_PAGE_SIZE;
			meta.hasInitiallyLoadedCompleted = true;
			commit( "commitmentsMetaUpdated", { meta, id, } );
		} catch ( e ) {
			meta.isLoadingOutstanding = false;
			commit( "commitmentsMetaUpdated", { id, meta, } );
			const message = i18n.t( "commitmentsStore.store_loadCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
	},
	refreshCommitments: async ( { commit, getters, }, { outcomeId, } ) => {
		// The situation here is still, ahem, developing. It should just be one meta
		// returned here, but treating it like an array until things solidify a bit more
		const metas = cloneDeep( getters.commitmentMetaContainingOutcomeId( outcomeId ) );
		if ( metas.length ) {
			metas.forEach( meta => {
				const { id, } = meta;
				const outstandingRequest = loadAllOutstandingOutcomes( {
					id,
					commitmentsTabType: meta.commitmentsTabType,
					outcomeStatus: [ OutcomeStatusV2.OUTSTANDING, OutcomeStatusV2.COMPLETE, ],
				} );
				commit( "commitmentsMetaUpdated", { id, meta, } );
				outstandingRequest.start();
			} );
		}
	},
	removeCommitment: async ( { commit, getters, }, { outcomeId, } ) => {
		// The situation here is still, ahem, developing. It should just be one meta
		// returned here, but treating it like an array until things solidify a bit more
		const metas = cloneDeep( getters.commitmentMetaContainingOutcomeId( outcomeId ) );
		if ( metas.length ) {
			metas.forEach( meta => {
				delete meta.commitments[ outcomeId ];
				commit( "commitmentsMetaUpdated", { id: meta.id, meta, } );
			} );
		}
	},
};

const mutations = {
	commitmentsMetaUpdated: ( state, { id, meta, } ) => {
		const commitmentsMap = state.commitmentsMap;
		state.commitmentsMap = {
			...commitmentsMap,
			...{ [ id ]: cloneDeep( meta ), },
		};
	},
};

export default {
	state: _state,
	getters: _getters,
	actions,
	mutations,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/commitments/index.js